import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useIntl } from 'react-intl'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { messages } from '../../messages'
import { useGetCarriersQuery } from '../../../../app/redux-fetch/apiOrders'
import { Route, LoadMetadata } from '../plannerTypes'

type ConsignmentRow = {
  loadId: number
  consignment: string
  type: string
  quantity: number
  tempControlled: string
  preferredCarrier: string
  schedArrival: string
  schedDeparture: string
}

type SplitRouteSidePanelProps = {
  open: boolean
  onClose: () => void
  onSplit: (
    selectedLoadIds: number[],
    carrierId: number | undefined,
    transitIdentifier: string,
    scheduledDeparture: string | undefined,
    scheduledArrival: string | undefined,
    splitType: 'onePerConsignment' | 'allConsignments'
  ) => void
  route: Route
  loadMetadataById: Record<number, LoadMetadata>
  tenantId: number
  transportModeId?: number
}

const SplitRouteSidePanel = ({
  open,
  onClose,
  onSplit,
  route,
  loadMetadataById,
  tenantId,
  transportModeId,
}: SplitRouteSidePanelProps) => {
  const { formatMessage } = useIntl()
  const [selectedCarrierId, setSelectedCarrierId] = useState<number | ''>('')
  const [transitIdentifier, setTransitIdentifier] = useState('')
  const [scheduledDeparture, setScheduledDeparture] = useState<Date | null>(null)
  const [scheduledArrival, setScheduledArrival] = useState<Date | null>(null)
  const [selectedLoadIds, setSelectedLoadIds] = useState<number[]>([])
  const [showConfirmation, setShowConfirmation] = useState(false)

  const { data: carriers = [] } = useGetCarriersQuery(
    { tenantId },
    { skip: !open || !tenantId }
  )

  const carrierOptions = useMemo(() => {
    if (transportModeId) {
      return carriers.filter((carrier) => carrier.transportModeId === transportModeId)
    }
    return carriers
  }, [carriers, transportModeId])

  const consignmentRows: ConsignmentRow[] = useMemo(() => {
    const loadIds = new Set<number>()
    route.destinations.forEach((dest) => {
      dest.loadActions.forEach((action) => {
        loadIds.add(action.loadId)
      })
    })

    return Array.from(loadIds).map((loadId) => {
      const metadata = loadMetadataById[loadId]
      return {
        loadId,
        consignment: metadata?.trackingNumber || '-',
        type: metadata?.loadTypeName || '-',
        quantity: metadata?.quantity || 0,
        tempControlled: '-',
        preferredCarrier: '-',
        schedArrival: '-',
        schedDeparture: '-',
      }
    })
  }, [route, loadMetadataById])

  const handleCarrierChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedCarrierId(event.target.value as number | '')
  }

  const handleTransitIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransitIdentifier(event.target.value)
  }

  const handleCheckboxChange = (loadId: number) => {
    setSelectedLoadIds((prev) =>
      prev.includes(loadId) ? prev.filter((id) => id !== loadId) : [...prev, loadId]
    )
  }

  const handleSplitClick = () => {
    if (selectedLoadIds.length > 0) {
      setShowConfirmation(true)
    }
  }

  const handleConfirmSplit = (splitType: 'onePerConsignment' | 'allConsignments') => {
    onSplit(
      selectedLoadIds,
      selectedCarrierId || undefined,
      transitIdentifier,
      scheduledDeparture?.toISOString(),
      scheduledArrival?.toISOString(),
      splitType
    )
    handleClose()
  }

  const handleClose = () => {
    setSelectedCarrierId('')
    setTransitIdentifier('')
    setScheduledDeparture(null)
    setScheduledArrival(null)
    setSelectedLoadIds([])
    setShowConfirmation(false)
    onClose()
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
  }

  const isSplitDisabled = selectedLoadIds.length === 0

  return (
    <SideModalWrapper
      open={open}
      onClose={handleClose}
      headerLabel={formatMessage(messages.splitRoute)}
      withBottomBox={false}
      width="700px"
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="split-carrier-label">
              {formatMessage(messages.carrierName)}
            </InputLabel>
            <Select
              labelId="split-carrier-label"
              value={selectedCarrierId}
              label={formatMessage(messages.carrierName)}
              onChange={handleCarrierChange}
            >
              <MenuItem value="">
                <em>{formatMessage(messages.selectCarrier)}</em>
              </MenuItem>
              {carrierOptions.map((carrier) => (
                <MenuItem key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label={formatMessage(messages.scheduledDeparture)}
            value={scheduledDeparture}
            onChange={(newValue) => setScheduledDeparture(newValue)}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
          />

          <DatePicker
            label={formatMessage(messages.scheduledArrival)}
            value={scheduledArrival}
            onChange={(newValue) => setScheduledArrival(newValue)}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          label={formatMessage(messages.transitIdentifier)}
          value={transitIdentifier}
          onChange={handleTransitIdentifierChange}
          sx={{ mb: 3 }}
        />

        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, px: 1 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.consignment)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.type)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.quantity)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.tempControlled)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.preferredCarrier)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.scheduledArrival)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.scheduledDeparture)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {consignmentRows.map((row) => (
              <TableRow key={row.loadId}>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.consignment}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.type}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.quantity}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.tempControlled}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.preferredCarrier}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.schedArrival}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.schedDeparture}</TableCell>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedLoadIds.includes(row.loadId)}
                    onChange={() => handleCheckboxChange(row.loadId)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button variant="contained" onClick={handleSplitClick} disabled={isSplitDisabled}>
            {formatMessage(messages.split)}
          </Button>
        </Box>
      </Box>

      {showConfirmation && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={handleCancelConfirmation}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 1,
              maxWidth: 500,
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ mb: 3, fontSize: '1rem' }}>
              {formatMessage(messages.splitRouteConfirmation)}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleConfirmSplit('onePerConsignment')}
              >
                {formatMessage(messages.oneRoutePerConsignment)}
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleConfirmSplit('allConsignments')}
              >
                {formatMessage(messages.oneRouteForAllConsignments)}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </SideModalWrapper>
  )
}

export default SplitRouteSidePanel
